import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  connectors as connectorsApi,
  devices,
  groups,
  type Connector,
  type DeviceInput,
  type DeviceSnmpConfig,
  type Group,
} from "../lib/api.js";
import { buildDeviceTree, flattenTree } from "../lib/device-tree.js";

/**
 * Add or edit a device.
 *
 * Credentials are write-only everywhere: the API never returns them, so on an
 * edit the field starts blank and means "leave the stored secret alone".
 * Pre-filling it with a placeholder would invite someone to save that
 * placeholder as the real community string.
 */

const VENDORS = [
  { value: "mikrotik", label: "MIKROTIK" },
  { value: "morningstar", label: "MORNINGSTAR" },
  { value: "tycon", label: "TYCON" },
  { value: "generic", label: "GENERIC" },
] as const;

/**
 * SNMPv3 protocols. Values match what the API accepts and the poller maps.
 * RouterOS names them the same way in /snmp community.
 */
const V3_AUTH = [
  { value: "md5", label: "MD5" },
  { value: "sha1", label: "SHA1" },
  { value: "sha224", label: "SHA224" },
  { value: "sha256", label: "SHA256" },
  { value: "sha384", label: "SHA384" },
  { value: "sha512", label: "SHA512" },
] as const;
const V3_PRIV = [
  { value: "des", label: "DES" },
  { value: "aes128", label: "AES (128-BIT)" },
  { value: "aes256", label: "AES-256" },
] as const;

/** Security level implied by the settings: encryption set means private. */
function v3Security(form: { snmp_v3_priv_protocol?: string | null }): "authorized" | "private" {
  return form.snmp_v3_priv_protocol ? "private" : "authorized";
}

/** Hint under a write-only secret, saying whether one is already stored. */
function storedHint(editing: boolean, isStored: boolean | undefined, v3 = false): string {
  const rule = v3 ? " At least 8 characters." : "";
  if (editing && isStored) return `Stored. Leave blank to keep it.${rule}`;
  if (editing) return `Not set.${rule}`;
  return `Stored encrypted; never shown again.${rule}`;
}

/** The power-supply choices the form offers, and the setting each stores. */
const PSU_CHOICES = {
  auto: null,
  "1": [1],
  "2": [2],
  both: [1, 2],
} as const satisfies Record<string, (1 | 2)[] | null>;
type PsuChoice = keyof typeof PSU_CHOICES;

function psuChoice(monitored: readonly (1 | 2)[] | null | undefined): PsuChoice {
  if (!monitored || monitored.length === 0) return "auto";
  if (monitored.length === 2) return "both";
  return monitored[0] === 2 ? "2" : "1";
}

export function DeviceFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [groupList, setGroupList] = useState<Group[]>([]);
  const [connectorList, setConnectorList] = useState<Connector[]>([]);
  const [form, setForm] = useState<DeviceInput>({
    name: "",
    address: "",
    vendor: "mikrotik",
    protocol: "snmp",
    poll_interval_seconds: 60,
    snmp_version: "v2c",
    snmp_port: 161,
    modbus_port: 502,
    modbus_unit_id: 1,
    enabled: true,
  });
  const [community, setCommunity] = useState("");
  // SNMPv3 passwords: like the community, write-only. Blank means "keep".
  const [authKey, setAuthKey] = useState("");
  const [privKey, setPrivKey] = useState("");
  const [stored, setStored] = useState<DeviceSnmpConfig | null>(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    groups.list().then((r) => setGroupList(r.groups)).catch(() => setGroupList([]));
    connectorsApi.list().then((r) => setConnectorList(r.connectors)).catch(() => setConnectorList([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    devices
      .get(id)
      .then((detail) => {
        const d = detail.device;
        setForm({
          name: d.name,
          address: d.address,
          vendor: d.vendor as DeviceInput["vendor"],
          protocol: d.protocol as DeviceInput["protocol"],
          notes: d.notes,
          enabled: d.enabled,
          poll_interval_seconds: d.poll_interval_seconds,
          group_id: d.group_id,
          connector_id: (d as { connector_id?: string | null }).connector_id ?? null,
          psu_monitored: d.psu_monitored,
          // Loaded so that saving an unrelated edit cannot quietly switch a v3
          // device back to v2c -- the form used to hard-code v2c.
          ...(detail.snmp
            ? {
                snmp_version: detail.snmp.snmp_version,
                snmp_port: detail.snmp.snmp_port,
                snmp_v3_username: detail.snmp.snmp_v3_username,
                snmp_v3_auth_protocol: detail.snmp.snmp_v3_auth_protocol,
                snmp_v3_priv_protocol: detail.snmp.snmp_v3_priv_protocol,
              }
            : {}),
        });
        setStored(detail.snmp);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load device"))
      .finally(() => setLoading(false));
  }, [id]);

  const set = <K extends keyof DeviceInput>(key: K, value: DeviceInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // An empty credential box means "unchanged", never "set it to empty".
      const payload: DeviceInput = { ...form };
      if (community.trim()) payload.snmp_community = community.trim();
      if (authKey) payload.snmp_v3_auth_key = authKey;
      if (privKey) payload.snmp_v3_priv_key = privKey;

      if (editing && id) {
        await devices.update(id, payload);
        navigate(`/devices/${id}`);
      } else {
        const created = await devices.create(payload);
        navigate(`/devices/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm(`Delete ${form.name}? Recorded metrics age out with retention.`)) return;
    setSaving(true);
    try {
      await devices.remove(id);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
      setSaving(false);
    }
  };

  if (loading) return <p style={{ color: "var(--text-muted)" }}>LOADING...</p>;

  const isSnmp = form.protocol === "snmp";

  return (
    <div style={{ maxWidth: 560 }}>
      <h1 style={{ fontSize: 15, margin: "0 0 20px", letterSpacing: "0.14em" }}>
        &gt; {editing ? "EDIT DEVICE" : "ADD DEVICE"}
      </h1>

      {error && (
        <p role="alert" style={errorStyle}>
          !! {error}
        </p>
      )}

      <form onSubmit={handleSubmit}>
        <Field label="NAME">
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} style={input} />
        </Field>

        <Field label="ADDRESS" hint="IP or hostname the poller connects to">
          <input required value={form.address} onChange={(e) => set("address", e.target.value)} style={input} />
        </Field>

        <Field label="VENDOR">
          <select
            value={form.vendor}
            onChange={(e) => set("vendor", e.target.value as DeviceInput["vendor"])}
            style={input}
          >
            {VENDORS.map((v) => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </Field>

        <Field label="PROTOCOL" hint="A TriStar answers on both; pick how to poll it">
          <select
            value={form.protocol}
            onChange={(e) => set("protocol", e.target.value as DeviceInput["protocol"])}
            style={input}
          >
            <option value="snmp">SNMP</option>
            <option value="modbus_tcp">MODBUS TCP</option>
          </select>
        </Field>

        <Field label="GROUP" hint="A device can sit at any level — a rack, a site, or the organisation itself">
          <select
            value={form.group_id ?? ""}
            onChange={(e) => set("group_id", e.target.value || null)}
            style={input}
          >
            <option value="">(UNFILED)</option>
            {/* Indented by depth: a flat list of names is ambiguous the moment
                two sites both contain a "Rack A". */}
            {flattenTree(buildDeviceTree(groupList, []).roots).map(({ node, depth }) => (
              <option key={node.group.id} value={node.group.id}>
                {`${"\u00a0\u00a0".repeat(depth)}${depth > 0 ? "└ " : ""}${node.group.name.toUpperCase()}`}
                {node.group.kind && node.group.kind !== "organization"
                  ? ` [${node.group.kind.toUpperCase()}]`
                  : ""}
              </option>
            ))}
          </select>
        </Field>

        {/* Only offered once a connector exists: an organization polling
            directly has nothing to choose between, and an empty picker reads
            as something being broken. */}
        {connectorList.length > 0 && (
          <Field
            label="REACHED VIA"
            hint="A connector polls from inside that network. Direct polls from Router Report itself, which only some organisations may do."
          >
            <select
              value={form.connector_id ?? ""}
              onChange={(e) => set("connector_id", e.target.value || null)}
              style={input}
            >
              <option value="">DIRECT — FROM ROUTER REPORT</option>
              {connectorList.map((c) => (
                <option key={c.id} value={c.id} disabled={!c.enrolled_at}>
                  {c.name.toUpperCase()}
                  {c.lan_address ? ` — ${c.lan_address}` : ""}
                  {c.enrolled_at ? (c.status === "online" ? "" : " (offline)") : " (not enrolled)"}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="POLL INTERVAL" hint="Seconds between polls (10-86400)">
          <input
            type="number"
            min={10}
            max={86400}
            value={form.poll_interval_seconds ?? 60}
            onChange={(e) => set("poll_interval_seconds", Number(e.target.value))}
            style={input}
          />
        </Field>

        {form.vendor === "mikrotik" && (
          <Field
            label="POWER SUPPLIES"
            hint="Which supplies to alert on. Auto watches every one that reports. On a router run from one supply, pick that one, or the empty slot alarms for ever."
          >
            <select
              value={psuChoice(form.psu_monitored)}
              onChange={(e) => set("psu_monitored", PSU_CHOICES[e.target.value as PsuChoice])}
              style={input}
            >
              <option value="auto">AUTO — EVERY SUPPLY THAT REPORTS</option>
              <option value="1">PSU 1 ONLY</option>
              <option value="2">PSU 2 ONLY</option>
              <option value="both">BOTH MUST BE OK</option>
            </select>
          </Field>
        )}

        {isSnmp ? (
          <>
            <Field label="SNMP PORT">
              <input
                type="number"
                min={1}
                max={65535}
                value={form.snmp_port ?? 161}
                onChange={(e) => set("snmp_port", Number(e.target.value))}
                style={input}
              />
            </Field>
            <Field
              label="SNMP VERSION"
              hint="v3 signs every request with a password, and can encrypt it. On MikroTik that is a community with security set to authorized or private."
            >
              <select
                value={form.snmp_version ?? "v2c"}
                onChange={(e) => set("snmp_version", e.target.value as "v2c" | "v3")}
                style={input}
              >
                <option value="v2c">v2c — COMMUNITY STRING</option>
                <option value="v3">v3 — USERNAME AND PASSWORDS</option>
              </select>
            </Field>

            {(form.snmp_version ?? "v2c") === "v2c" ? (
              <Field
                label="SNMP COMMUNITY"
                hint={storedHint(editing, stored?.has_community)}
              >
                <input
                  type="password"
                  autoComplete="new-password"
                  value={community}
                  onChange={(e) => setCommunity(e.target.value)}
                  style={input}
                />
              </Field>
            ) : (
              <>
                <Field label="USERNAME" hint="On MikroTik, the community's name">
                  <input
                    value={form.snmp_v3_username ?? ""}
                    onChange={(e) => set("snmp_v3_username", e.target.value || null)}
                    autoComplete="off"
                    style={input}
                  />
                </Field>
                <Field label="SECURITY" hint="Must match the community's security setting on the router">
                  <select
                    value={v3Security(form)}
                    onChange={(e) => {
                      const level = e.target.value as "authorized" | "private";
                      if (!form.snmp_v3_auth_protocol) set("snmp_v3_auth_protocol", "sha1");
                      if (level === "private") {
                        if (!form.snmp_v3_priv_protocol) set("snmp_v3_priv_protocol", "aes128");
                      } else {
                        set("snmp_v3_priv_protocol", null);
                        setPrivKey("");
                      }
                    }}
                    style={input}
                  >
                    <option value="authorized">AUTHORIZED — SIGNED</option>
                    <option value="private">PRIVATE — SIGNED AND ENCRYPTED</option>
                  </select>
                </Field>
                <Field label="AUTHENTICATION PROTOCOL">
                  <select
                    value={form.snmp_v3_auth_protocol ?? "sha1"}
                    onChange={(e) => set("snmp_v3_auth_protocol", e.target.value)}
                    style={input}
                  >
                    {V3_AUTH.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="AUTHENTICATION PASSWORD" hint={storedHint(editing, stored?.has_v3_auth_key, true)}>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={authKey}
                    onChange={(e) => setAuthKey(e.target.value)}
                    style={input}
                  />
                </Field>
                {v3Security(form) === "private" && (
                  <>
                    <Field label="ENCRYPTION PROTOCOL">
                      <select
                        value={form.snmp_v3_priv_protocol ?? "aes128"}
                        onChange={(e) => set("snmp_v3_priv_protocol", e.target.value)}
                        style={input}
                      >
                        {V3_PRIV.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="ENCRYPTION PASSWORD" hint={storedHint(editing, stored?.has_v3_priv_key, true)}>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={privKey}
                        onChange={(e) => setPrivKey(e.target.value)}
                        style={input}
                      />
                    </Field>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Field label="MODBUS PORT">
              <input
                type="number"
                min={1}
                max={65535}
                value={form.modbus_port ?? 502}
                onChange={(e) => set("modbus_port", Number(e.target.value))}
                style={input}
              />
            </Field>
            <Field
              label="MODBUS UNIT ID"
              hint="Not always 1 — a TriStar behind an EMC-1 gateway answers on its own id"
            >
              <input
                type="number"
                min={0}
                max={255}
                value={form.modbus_unit_id ?? 1}
                onChange={(e) => set("modbus_unit_id", Number(e.target.value))}
                style={input}
              />
            </Field>
          </>
        )}

        <Field label="NOTES">
          <input
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value || null)}
            style={input}
          />
        </Field>

        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.enabled ?? true}
            onChange={(e) => set("enabled", e.target.checked)}
          />
          <span style={{ color: "var(--text-secondary)" }}>POLLING ENABLED</span>
        </label>

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button type="submit" disabled={saving} style={primaryButton}>
            {saving ? "[ SAVING... ]" : editing ? "[ SAVE ]" : "[ ADD DEVICE ]"}
          </button>
          <button type="button" onClick={() => navigate(-1)} style={secondaryButton}>
            [ CANCEL ]
          </button>
          {editing && (
            <button type="button" onClick={() => void handleDelete()} style={dangerButton}>
              [ DELETE ]
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", letterSpacing: "0.08em" }}>
        &gt; {label}
      </label>
      {hint && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", margin: "2px 0 4px" }}>{hint}</div>
      )}
      <div style={{ marginTop: hint ? 0 : 4 }}>{children}</div>
    </div>
  );
}

const input: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 14,
  border: "1px solid var(--border)",
  background: "var(--page-plane)",
  color: "var(--text-primary)",
};

const primaryButton: React.CSSProperties = {
  padding: "9px 16px",
  fontSize: 13,
  letterSpacing: "0.1em",
  color: "var(--page-plane)",
  background: "var(--text-primary)",
  border: "1px solid var(--text-primary)",
};

const secondaryButton: React.CSSProperties = {
  padding: "9px 16px",
  fontSize: 13,
  letterSpacing: "0.1em",
  color: "var(--text-secondary)",
  background: "transparent",
  border: "1px solid var(--border)",
};

const dangerButton: React.CSSProperties = {
  ...secondaryButton,
  color: "var(--status-critical)",
  border: "1px solid var(--status-critical)",
  marginLeft: "auto",
};

const errorStyle: React.CSSProperties = {
  color: "var(--status-critical)",
  fontSize: 13,
  padding: "8px 10px",
  border: "1px solid var(--status-critical)",
  marginBottom: 16,
};
